import * as React from 'react'
import moment from 'moment'
import { UserListItem, UserRoleAssignment } from '@/lib/api/company/configuration/adminPanel/users'
import { useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { RoleManagementPopover, companyRolesOf } from './RoleManagementPopover'
import { RoleBadge } from '../roles/RoleBadge'
import { ServiceAccountRoleCell } from '@/modules/shared/components/serviceAccount/ServiceAccountUserRoles'
import { ServiceAccountUser } from '@/modules/shared/query-client/useServiceAccountAdmin'
import type { GroupModel } from '@/app/models/groupModel'
import type { DriverGroupAccess } from '@/lib/api/company/driverGroupAccess'
import { GroupAccessCell } from './GroupAccessCell'

interface UserRowProps {
  user: UserListItem
  companyId: string
  onRolesUpdate: (user: UserListItem, roles: UserRoleAssignment[]) => Promise<void>
  onDoubleClick?: () => void
  // Set when the company is a service account: access comes from its roster, roles are read-only.
  serviceAccount?: { membership?: ServiceAccountUser }
  driverAccess?: {
    access: DriverGroupAccess | undefined
    driverGroups: GroupModel[]
    vehicleGroups: Pick<GroupModel, 'id' | 'name'>[]
    driverOnly?: { driverName: string | null; hasDriver: boolean }
  }
}

const UserRow: React.FC<UserRowProps> = ({ user, companyId, onRolesUpdate, onDoubleClick, serviceAccount, driverAccess }) => {
  const { t } = useLocalize()
  const { toggleId, selectedGroup, isSelectedAny } = useTable()
  const [isHovered, setIsHovered] = React.useState(false)

  const isChecked = selectedGroup?.ids.includes(user.id) ?? false
  const isActive = true // All users are considered active for selection purposes
  const isDisabled = isSelectedAny && selectedGroup?.isActive !== undefined && selectedGroup.isActive !== isActive

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const handleRowClick = () => {
    toggleId(user.id, isActive)
  }

  const getStatusBadge = () => {
    const isActive = !!user.lastLogin && !user.locked
    return (
      <span className={`tw-inline-flex tw-w-3 tw-h-3 tw-rounded-full tw-ring-2 tw-ring-offset-1 ${isActive ? 'tw-bg-green-500 tw-ring-green-300' : 'tw-bg-red-400 tw-ring-red-200'}`}></span>
    )
  }

  const getRoleBadgeColor = (roleName: string) => {
    const lowerRoleName = roleName.toLowerCase()
    if (lowerRoleName.includes('admin') || lowerRoleName.includes('administrator')) {
      return 'badge-primary'
    }
    if (lowerRoleName.includes('menadżer') || lowerRoleName.includes('manager')) {
      return 'badge-secondary'
    }
    return 'badge-accent'
  }

  const getUserRolesBadges = () => {
    const companyRoles = companyRolesOf(user, companyId)
    if (serviceAccount) {
      return <ServiceAccountRoleCell roles={companyRoles} membership={serviceAccount.membership} />
    }
    if (companyRoles.length === 0) {
      return (
        <div className="tw-inline-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-text-gray-500">-</span>
          <RoleManagementPopover
            user={user}
            companyId={companyId}
            onRolesUpdate={onRolesUpdate}
          />
        </div>
      )
    }
    
    return (
      <div className="tw-inline-flex tw-items-center tw-gap-1">
        <div className="tw-inline-flex tw-flex-wrap tw-gap-1">
          {companyRoles.map((role) => (
            <RoleBadge
              key={role.id}
              roleId={role.id}
              name={role.name}
              companyId={companyId}
              className={`badge badge-sm ${getRoleBadgeColor(role.name)}`}
            />
          ))}
        </div>
        <RoleManagementPopover
          user={user}
          companyId={companyId}
          onRolesUpdate={onRolesUpdate}
        />
      </div>
    )
  }

  const formatDate = (date: string) => {
    if (!date) return '-'
    return moment(date).format('DD.MM.YYYY HH:mm')
  }

  return (
    <tr
      className={`tw-cursor-pointer tw-border-b tw-border-gray-100 tw-bg-white hover:tw-bg-primary/10 ${
        isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''
      } ${isDisabled ? 'tw-opacity-50' : ''}`}
      onClick={handleRowClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <td className={`tw-w-12 tw-px-4 tw-py-3 ${isHovered ? 'tw-bg-white/0' : ''}`}>
        <label className="tw-inline-flex tw-items-center">
          <Checkbox
            onChange={() => toggleId(user.id, isActive)}
            disabled={isDisabled}
            checked={isChecked}
          />
        </label>
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-center">
        {getStatusBadge()}
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
        {user.login}
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
        {getUserRolesBadges()}
      </td>
      {driverAccess &&
        (['drivers', 'vehicles'] as const).map((kind) => (
          <td key={kind} className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
            <GroupAccessCell
              kind={kind}
              userId={user.id}
              isGlobalAdmin={!!user.globalAdmin}
              companyId={companyId}
              access={driverAccess.access}
              groups={kind === 'drivers' ? driverAccess.driverGroups : driverAccess.vehicleGroups}
              driverOnly={driverAccess.driverOnly}
            />
          </td>
        ))}
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
        {user.createdBy || '-'}
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
        {formatDate(user.createdDate)}
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
        {user.lastLogin ? formatDate(user.lastLogin) : '-'}
      </td>
    </tr>
  )
}

export default UserRow

